import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureScreenshot,
  clearDraft,
  fetchBugs,
  loadDraft,
  loadReporter,
  loadSeenComments,
  markCommentsSeen,
  saveDraft,
  saveReporter,
  sendReport,
  type SeenComments,
  startConsoleCapture,
  startNetworkCapture,
  type BugsResponse,
  type ConsoleCapture,
  type ConsoleEntry,
  type NetworkCapture,
  type NetworkEntry,
  type ScreenshotResult,
  type TargetInfo,
} from "../core";
import { Inspector } from "./Inspector";
import { Panel, type ReporterSuccessView } from "./Panel";
import { RegionCapture, type Region } from "./RegionCapture";
import { TrackingPanel, hasUnseenTeamComment } from "./TrackingPanel";
import type { PanelTab } from "./chrome";

export interface BugWidgetProps {
  /** Clé publique du projet (attribut data-api-key du tag script). */
  apiKey: string;
  /** Origine du tracker — déduite de l'URL du script widget.js. */
  trackerBase: string;
  /** Version/commit déployé, joint aux métadonnées des rapports. */
  commitSha?: string;
}

type Phase = "idle" | "menu" | "capturing" | "inspecting" | "form" | "tracking";

const PENDING = "PENDING_CLIENT_VALIDATION";

function BugIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m8 2 1.88 1.88M14.12 3.88 16 2M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
      <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6ZM12 20v-9" />
      <path d="M6.53 9C4.6 8.8 3 7.1 3 5M6 13H2M3 21c0-2.1 1.7-3.9 3.8-4M20.97 5c0 2.1-1.6 3.8-3.5 4M22 13h-4M17.2 17c2.1.1 3.8 1.9 3.8 4" />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61M2 2l20 20" />
    </svg>
  );
}

export function BugWidget({ apiKey, trackerBase, commitSha }: BugWidgetProps) {
  const endpoint = `${trackerBase}/api/reports`;

  const [phase, setPhase] = useState<Phase>("idle");
  const [hidden, setHidden] = useState(false);
  // Écran tactile : pas de cliquer-glisser confortable → capture auto du
  // viewport entier au lieu de l'outil de sélection de zone.
  const [coarsePointer] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches,
  );
  const [target, setTarget] = useState<TargetInfo | null>(null);
  const [screenshot, setScreenshot] = useState<ScreenshotResult | null>(null);
  const [screenshotFailed, setScreenshotFailed] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [consoleLogs, setConsoleLogs] = useState<ConsoleEntry[]>([]);
  const [networkLogs, setNetworkLogs] = useState<NetworkEntry[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<ReporterSuccessView | null>(null);

  // Suivi des bugs — état partagé entre le badge et le panneau.
  const [bugs, setBugs] = useState<BugsResponse | null>(null);
  const [bugsLoading, setBugsLoading] = useState(false);
  const [bugsError, setBugsError] = useState<string | null>(null);
  // Commentaires de l'équipe déjà consultés (persisté par navigateur).
  const [seenComments, setSeenComments] = useState<SeenComments>(() => loadSeenComments());

  const consoleRef = useRef<ConsoleCapture | null>(null);
  const networkRef = useRef<NetworkCapture | null>(null);
  const bugsInFlight = useRef(false);

  const reloadBugs = useCallback(() => {
    if (bugsInFlight.current) return;
    bugsInFlight.current = true;
    setBugsLoading(true);
    void fetchBugs(trackerBase, apiKey).then((result) => {
      bugsInFlight.current = false;
      setBugsLoading(false);
      if (result.ok) {
        setBugs(result.data);
        setBugsError(null);
      } else {
        setBugsError(result.message);
      }
    });
  }, [trackerBase, apiKey]);

  // Instrumentation dès le montage — indispensable pour capter les logs
  // émis avant toute interaction avec le widget.
  useEffect(() => {
    consoleRef.current = startConsoleCapture();
    networkRef.current = startNetworkCapture();
    networkRef.current.ignoreUrl(endpoint);
    networkRef.current.ignoreUrl(`${trackerBase}/api/widget/`);
  }, [endpoint, trackerBase]);

  // Un seul chargement au montage pour alimenter le badge « à valider »
  // (pas de polling : recharge à l'ouverture du panneau et après une action).
  useEffect(() => {
    reloadBugs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Brouillon + identité mémorisée.
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      setTitle(draft.title);
      setDescription(draft.description);
    }
    const stored = loadReporter();
    if (stored) {
      setReporterName(stored.name);
      setReporterEmail(stored.email);
    }
  }, []);

  useEffect(() => {
    if (title || description) saveDraft({ title, description });
  }, [title, description]);

  // Échap : ferme le panneau courant (le mode inspection gère le sien).
  useEffect(() => {
    if (phase !== "menu" && phase !== "form" && phase !== "tracking") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPhase("idle");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase]);

  const snapshotLogs = useCallback(() => {
    setConsoleLogs(consoleRef.current?.snapshot() ?? []);
    setNetworkLogs(networkRef.current?.snapshot() ?? []);
  }, []);

  const takeScreenshot = useCallback(async (region?: Region) => {
    const result = await captureScreenshot(region);
    setScreenshot(result);
    setScreenshotFailed(result === null);
    if (result) setIncludeScreenshot(true);
  }, []);

  const startReport = useCallback(() => {
    setError(null);
    setSuccess(null);
    setTarget(null);
    setScreenshot(null);
    setScreenshotFailed(false);
    snapshotLogs();
    if (coarsePointer) {
      // Mobile : capture automatique du viewport, direction le formulaire.
      setPhase("form");
      void takeScreenshot();
    } else {
      setPhase("capturing");
    }
  }, [snapshotLogs, coarsePointer, takeScreenshot]);

  const finishRegionCapture = useCallback(
    (region?: Region) => {
      setPhase("form");
      void takeScreenshot(region);
    },
    [takeScreenshot],
  );

  // « Reprendre » / « Ajouter une capture » depuis le formulaire.
  const requestCapture = useCallback(() => {
    if (coarsePointer) void takeScreenshot();
    else setPhase("capturing");
  }, [coarsePointer, takeScreenshot]);

  const finishInspection = useCallback((selected: TargetInfo | null) => {
    if (selected) setTarget(selected);
    setPhase("form");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!description.trim() || sending) return;
    setSending(true);
    setError(null);

    const trimmedName = reporterName.trim();
    const trimmedEmail = reporterEmail.trim();
    const result = await sendReport(
      endpoint,
      {
        projectKey: apiKey,
        ...(title.trim() ? { title: title.trim() } : {}),
        description: description.trim(),
        pageUrl: window.location.href,
        ...(target ? { selector: target.selector } : {}),
        ...(target?.componentName ? { componentName: target.componentName } : {}),
        consoleLogs,
        networkLogs,
        metadata: {
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          userAgent: navigator.userAgent,
          ...(commitSha ? { commitSha } : {}),
        },
        ...(trimmedName ? { reporterName: trimmedName } : {}),
        ...(trimmedEmail ? { reporterEmail: trimmedEmail } : {}),
      },
      includeScreenshot && screenshot ? screenshot.blob : null,
    );

    setSending(false);
    if (result.ok) {
      setSuccess({ bug: result.bug, screenshotDropped: result.screenshotDropped });
      clearDraft();
      setTitle("");
      setDescription("");
      if (trimmedName || trimmedEmail) {
        saveReporter({ name: trimmedName, email: trimmedEmail });
      }
      reloadBugs();
    } else {
      setError(result.message);
    }
  }, [
    description, sending, endpoint, apiKey, title, target, consoleLogs, networkLogs,
    commitSha, reporterName, reporterEmail, includeScreenshot, screenshot, reloadBugs,
  ]);

  // Mise à jour optimiste après une validation : le statut renvoyé par l'API
  // est appliqué immédiatement, sans attendre le rechargement de la liste.
  const applyBugStatus = useCallback((bugId: string, status: string) => {
    setBugs((prev) =>
      prev
        ? { ...prev, bugs: prev.bugs.map((b) => (b.id === bugId ? { ...b, status } : b)) }
        : prev,
    );
  }, []);

  const switchTab = useCallback(
    (tab: PanelTab) => {
      if (tab === "tracking") {
        setPhase("tracking");
      } else {
        // Onglet « Signaler » depuis le suivi : on repart du choix d'élément.
        startReport();
      }
    },
    [startReport],
  );

  const markSeen = useCallback((bugId: string, lastTeamCommentAt: string) => {
    setSeenComments(markCommentsSeen(bugId, lastTeamCommentAt));
  }, []);

  const pendingCount = bugs?.bugs.filter((b) => b.status === PENDING).length ?? 0;
  // Bugs avec un commentaire de l'équipe pas encore consulté sur ce navigateur.
  const unseenCount =
    bugs?.bugs.filter((b) => hasUnseenTeamComment(b, seenComments)).length ?? 0;
  const badgeCount = pendingCount + unseenCount;

  if (hidden) return null;

  return (
    <div data-bug-widget="">
      {phase === "capturing" && (
        <RegionCapture
          onCapture={(region) => finishRegionCapture(region)}
          onCaptureFull={() => finishRegionCapture()}
          onSkip={() => setPhase("form")}
        />
      )}

      {phase === "inspecting" && (
        <Inspector
          onSelect={finishInspection}
          onSkip={() => finishInspection(null)}
          onCancel={() => setPhase("form")}
        />
      )}

      {phase === "form" && (
        <Panel
          target={target}
          screenshot={screenshot}
          screenshotFailed={screenshotFailed}
          includeScreenshot={includeScreenshot}
          onIncludeScreenshotChange={setIncludeScreenshot}
          onRetakeScreenshot={requestCapture}
          onPickTarget={() => setPhase("inspecting")}
          onClearTarget={() => setTarget(null)}
          consoleLogs={consoleLogs}
          networkLogs={networkLogs}
          title={title}
          onTitleChange={setTitle}
          description={description}
          onDescriptionChange={setDescription}
          reporterName={reporterName}
          onReporterNameChange={setReporterName}
          reporterEmail={reporterEmail}
          onReporterEmailChange={setReporterEmail}
          reporterLocked={false}
          sending={sending}
          error={error}
          success={success}
          badgeCount={badgeCount}
          onSwitchTab={switchTab}
          onSubmit={() => void handleSubmit()}
          onClose={() => setPhase("idle")}
        />
      )}

      {phase === "tracking" && (
        <TrackingPanel
          trackerBase={trackerBase}
          apiKey={apiKey}
          bugs={bugs}
          bugsLoading={bugsLoading}
          bugsError={bugsError}
          reloadBugs={reloadBugs}
          applyBugStatus={applyBugStatus}
          badgeCount={badgeCount}
          seenComments={seenComments}
          markSeen={markSeen}
          onSwitchTab={switchTab}
          onClose={() => setPhase("idle")}
        />
      )}

      {(phase === "idle" || phase === "menu") && (
        <div className="bw-fab-zone">
          {phase === "menu" && (
            <div className="bw-menu" role="menu">
              <button type="button" className="bw-menu-item" role="menuitem" onClick={startReport}>
                <FlagIcon />
                Signaler un bug
              </button>
              <div className="bw-menu-sep" />
              <button
                type="button"
                className="bw-menu-item"
                role="menuitem"
                onClick={() => setPhase("tracking")}
              >
                <ListIcon />
                Suivi des bugs
                {(pendingCount > 0 || unseenCount > 0) && (
                  <span className="bw-menu-badges">
                    {pendingCount > 0 && (
                      <span className="bw-menu-badge">{pendingCount} à valider</span>
                    )}
                    {unseenCount > 0 && (
                      <span className="bw-menu-badge bw-menu-badge-info">
                        {unseenCount} commentaire{unseenCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </span>
                )}
              </button>
              <div className="bw-menu-sep" />
              <button
                type="button"
                className="bw-menu-item bw-menu-item-muted"
                role="menuitem"
                onClick={() => setHidden(true)}
              >
                <EyeOffIcon />
                Masquer jusqu’au rechargement
              </button>
            </div>
          )}
          <div className="bw-fab-wrap">
            <button
              type="button"
              className="bw-fab"
              aria-label="Signalement de bugs"
              title="Signaler un bug"
              onClick={() => setPhase(phase === "menu" ? "idle" : "menu")}
            >
              <BugIcon />
            </button>
            {badgeCount > 0 && <span className="bw-fab-badge">{badgeCount}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
